// Return all elements of `array` that have a fuzzy
// match against `pattern`.
const simpleFilter = function (pattern: string, array: string[]) {
	return array.filter(function (str) {
		return test(pattern, str);
	});
};

// Does `pattern` fuzzy match `str`?
const test = function (pattern: string, str: string) {
	return match(pattern, str) !== null;
};
// If `pattern` matches `str`, wrap each matching character
// in `opts.pre` and `opts.post`. If no match, return null

interface MatchOptions {
	pre?: string;
	post?: string;
	caseSensitive?: boolean;
	extract?: (el: any) => string;
	skipSort?: boolean;
}

interface MatchResult {
	rendered: string;
	score: number;
}

interface FilterResult {
	string: string;
	score: number;
	index: number;
	original: any;
}

const match = function (pattern: string, str: string, opts?: MatchOptions): MatchResult | null {
	if (Array.isArray(str)) {
		const matches: Array<MatchResult | null> = str.map(s => match(pattern, s, opts));
		const matchFound = matches[0] || { rendered: str[0], score: 0 };
		matches.forEach(m => {
			matchFound.score = Math.max(matchFound.score, m && m.score ? m.score : 0);
		});
		return matchFound;
	}

	opts = opts || {};
	let patternIdx = 0,
		totalScore = 0,
		currScore = 0,
		ch;
	const result: any[] = [],
		len = str.length,
		// prefix
		pre = opts.pre || '',
		// suffix
		post = opts.post || '',
		// String to compare against. This might be a lowercase version of the
		// raw string
		compareString = (opts.caseSensitive && str) || str.toLowerCase();

	pattern = (opts.caseSensitive && pattern) || pattern.toLowerCase();

	// For each character in the string, either add it to the result
	// or wrap in template if it's the next string in the pattern
	for (let idx = 0; idx < len; idx++) {
		ch = str[idx];
		if (compareString[idx] === pattern[patternIdx]) {
			ch = pre + ch + post;
			patternIdx += 1;

			// consecutive characters should increase the score more than linearly
			currScore += 1 + currScore;
		} else {
			currScore = 0;
		}
		totalScore += currScore;
		result[result.length] = ch;
	}

	// return rendered string if we have a match for every char
	if (patternIdx === pattern.length) {
		// if the string is an exact match with pattern, totalScore should be maxed
		totalScore = compareString === pattern ? Infinity : totalScore;
		return { rendered: result.join(''), score: totalScore };
	}

	return null;
};

// The normal entry point. Filters `arr` for matches against `pattern`.
// It returns an array with matching values of the type:
//
//     [{
//         string:   '<b>lah' // The rendered string
//       , index:    2        // The index of the element in `arr`
//       , original: 'blah'   // The original element in `arr`
//     }]
//
// `opts` is an optional argument bag. Details:
//
//    opts = {
//        // string to put before a matching character
//        pre:     '<b>'
//
//        // string to put after matching character
//      , post:    '</b>'
//
//        // Optional function. Input is an entry in the given arr`,
//        // output should be the string to test `pattern` against.
//        // In this example, if `arr = [{crying: 'koala'}]` we would return
//        // 'koala'.
//      , extract: function(arg) { return arg.crying; }
//    }
const filter = function (pattern: string, arr: Array<any>, opts: MatchOptions) {
	if (!arr || arr.length === 0) {
		return [];
	}
	if (typeof pattern !== 'string') {
		return arr;
	}
	opts = opts || {};
	const newArr = arr.reduce(function (prev: FilterResult[], element, idx) {
		let str = '';
		if (typeof element !== 'string' && opts.extract) {
			str = opts.extract(element);
		} else if (typeof element === 'string') {
			str = element;
		}
		const rendered = match(pattern, str, opts);
		if (rendered != null) {
			prev[prev.length] = {
				string: rendered.rendered,
				score: rendered.score,
				index: idx,
				original: element,
			};
		}
		return prev;
	}, []);
	if (!opts.skipSort) {
		newArr.sort(function (a, b) {
			const compare = b.score - a.score;
			if (compare) return compare;
			return a.index - b.index;
		});
	}
	return newArr;
};

export default {
	simpleFilter,
	test,
	match,
	filter,
};
